import type * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import DatePicker from '@cloudscape-design/components/date-picker';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import {
  ALL_AUDIT_ACTOR_TYPES,
  ALL_AUDIT_BILLING_ACTIONS,
  type AuditActorType,
  type AuditBillingAction,
  type AuditBillingEvent,
  type AuditBillingFilters,
} from '@etiquetachef/types';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  fetchAuditBillingPage,
  type AuditBillingPage as PageResult,
} from '@/lib/firestoreAuditBilling';
import { exportAuditBillingCallable } from '@/lib/callables';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { serverNow } from '@etiquetachef/ui-shared/online';

const DEFAULT_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const ALL = '__ALL__';

type FetchState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; rows: AuditBillingEvent[]; cursor: QueryDocumentSnapshot | null; hasMore: boolean }
  | { kind: 'ERROR'; error: unknown };

/**
 * Tela `/audit/billing`: log financeiro auditável, com filtros na URL,
 * paginação por cursor, detalhe do evento e export CSV.
 *
 * Origem: sem issue — commit 6b847501f, 2026-05-26 00:56 +0200 (Sprint 35
 * PR H1 — /audit/billing tela + filtros + export CSV).
 *
 * Por quê: os filtros vivem na query string (`from`, `to`, `tenantId`,
 * `action`, `actorType`, `extEventId`) para o link ser compartilhável e
 * sobreviver a reload; o default é a janela dos últimos 30 dias.
 *
 * Contrato: lista via `fetchAuditBillingPage` (client SDK, 50 por página;
 * "Carregar mais" concatena). Export via callable `exportAuditBilling`
 * (shim `requireSuperAdmin` — qualquer papel admin, `docs/permissions.md`;
 * até 50 000 linhas, acima disso `resource-exhausted`) → baixa
 * `audit-billing-<ISO>.csv` no navegador. Sem escrita no Firestore.
 *
 * Usado por: `routes/router.tsx` (rota `audit/billing`, dentro do
 * `RequireSuperAdmin`), menu Sistema → "Auditoria · Billing"
 * (`AdminLayout`). Só `apps/admin` (web); sem equivalente Android/iOS.
 * Spec: `docs/screens/audit-billing.md`.
 *
 * Arquitetura: leitura de `audit/billing/items` autorizada pela regra
 * `match /audit/billing/items/{eventId}` (Lê: super-admin). Toda combinação
 * de filtro tem índice composto — ver `fetchAuditBillingPage`.
 *
 * Dados: `AuditBillingEvent` (`packages/types/src/schemas.ts`). Valor na
 * coluna é `amountCents / 100` com 2 casas, prefixado pelo código da moeda
 * (`BRL 129.90`) — `amountCents` em CENTAVOS; `null` vira "—". Datas dos
 * filtros são dias UTC (`Date.parse('AAAA-MM-DD')`), `to` inclusivo até
 * 23:59:59.999 UTC.
 *
 * Falhas conhecidas:
 *  - Até 2026-10-13 (#935, item 16; achado da #798): o ramo "export grande
 *    demais" testava `msg.includes('resource-exhausted')`, mas a mensagem da
 *    `HttpsError` é `export_too_large: …` e o código fica em `err.code`
 *    (`functions/resource-exhausted`) — o aviso específico nunca aparecia.
 *    As falhas de export e de "carregar mais" também só iam ao console; hoje
 *    vão ao PostHog (`admin_audit_billing_export_failed`/`_load_more_failed`).
 *
 * Dicas: `action`/`actorType` da URL entram com cast, sem validar contra o
 * enum — valor desconhecido só devolve lista vazia.
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-13 10:31 +0200 — (#935, item 16) export grande lido por
 *    `err.code`; falhas reportadas ao PostHog (item 14).
 */
export function AuditBillingPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<AuditBillingFilters>(() => parseFilters(searchParams), [searchParams]);
  const [state, setState] = useState<FetchState>({ kind: 'LOADING' });
  const [selected, setSelected] = useState<AuditBillingEvent | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchInitial = useCallback(async () => {
    setState({ kind: 'LOADING' });
    try {
      const page: PageResult = await fetchAuditBillingPage(filters, null);
      setState({ kind: 'READY', rows: page.rows, cursor: page.cursor, hasMore: page.hasMore });
    } catch (err) {
      setState({ kind: 'ERROR', error: err });
    }
  }, [filters]);

  useEffect(() => {
    void fetchInitial();
  }, [fetchInitial]);

  /** Busca a próxima página a partir do cursor e concatena; falha vira toast. */
  async function loadMore(): Promise<void> {
    if (state.kind !== 'READY' || !state.hasMore) return;
    try {
      const page = await fetchAuditBillingPage(filters, state.cursor);
      setState({
        kind: 'READY',
        rows: [...state.rows, ...page.rows],
        cursor: page.cursor,
        hasMore: page.hasMore,
      });
    } catch (err) {
      // #935 (item 14): só `console.error` não chega até nós — o admin não
      // manda DevTools. O report leva o erro para o PostHog.
      reportNonFatal('admin_audit_billing_load_more_failed', serializeError(err));
      toast.error(t('auditBilling.error.loadMore'));
    }
  }

  /** Gera o CSV na callable `exportAuditBilling` com os filtros atuais e baixa. */
  async function exportCsv(): Promise<void> {
    setExporting(true);
    try {
      const out = await exportAuditBillingCallable(filters);
      downloadCsv(out.csv, out.generatedAt);
      toast.success(t('auditBilling.export.success', { count: out.rows }));
    } catch (err) {
      // Bug corrigido em 2026-10-13 10:31 +0200 (#935, item 16): lia
      // `err.message.includes('resource-exhausted')`, mas a mensagem é
      // `export_too_large: …` — o código vem em `err.code`, com o prefixo
      // `functions/` do SDK. O aviso "grande demais" nunca aparecia. Mesma
      // classe da #710 (`AddonEditorDialog`, discriminador lido da mensagem).
      const code = callableErrorCode(err);
      if (code === 'resource-exhausted') {
        toast.error(t('auditBilling.export.tooLarge'));
      } else {
        toast.error(t('auditBilling.export.error'));
      }
      reportNonFatal('admin_audit_billing_export_failed', { code, ...serializeError(err) });
    } finally {
      setExporting(false);
    }
  }

  /** Grava um filtro na query string (`null`/vazio remove; número vira AAAA-MM-DD). */
  function updateFilter<K extends keyof AuditBillingFilters>(
    key: K,
    value: AuditBillingFilters[K],
  ): void {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      next.delete(filterKeyToParam(key));
    } else if (typeof value === 'number') {
      next.set(filterKeyToParam(key), new Date(value).toISOString().slice(0, 10));
    } else {
      next.set(filterKeyToParam(key), String(value));
    }
    setSearchParams(next, { replace: true });
  }

  const dateLocale = i18n.language;
  const actionOptions = [
    { value: ALL, label: t('auditBilling.filter.actionAll') },
    ...ALL_AUDIT_BILLING_ACTIONS.map((a) => ({ value: a, label: a })),
  ];
  const actorOptions = [
    { value: ALL, label: t('auditBilling.filter.actorTypeAll') },
    ...ALL_AUDIT_ACTOR_TYPES.map((a) => ({ value: a, label: a })),
  ];

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('auditBilling.subtitle')}
          actions={
            <Button
              iconName="download"
              loading={exporting}
              disabled={state.kind !== 'READY'}
              onClick={(): void => {
                void exportCsv();
              }}
            >
              {t('auditBilling.export.button')}
            </Button>
          }
        >
          {t('auditBilling.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container>
          <ColumnLayout columns={3}>
            <FormField label={t('auditBilling.filter.tenant')}>
              <Input
                value={filters.tenantId ?? ''}
                onChange={({ detail }): void => {
                  updateFilter('tenantId', detail.value || null);
                }}
                placeholder={t('auditBilling.filter.tenantPlaceholder')}
              />
            </FormField>
            <FormField label={t('auditBilling.filter.action')}>
              <Select
                selectedOption={actionOptions.find((o) => o.value === (filters.action ?? ALL)) ?? null}
                options={actionOptions}
                onChange={({ detail }): void => {
                  const v = detail.selectedOption.value;
                  updateFilter('action', v === ALL ? null : (v as AuditBillingAction));
                }}
              />
            </FormField>
            <FormField label={t('auditBilling.filter.actorType')}>
              <Select
                selectedOption={actorOptions.find((o) => o.value === (filters.actorType ?? ALL)) ?? null}
                options={actorOptions}
                onChange={({ detail }): void => {
                  const v = detail.selectedOption.value;
                  updateFilter('actorType', v === ALL ? null : (v as AuditActorType));
                }}
              />
            </FormField>
            <FormField label={t('auditBilling.filter.from')}>
              <DatePicker
                value={dateToInput(filters.from)}
                onChange={({ detail }): void => {
                  const ts = Date.parse(detail.value);
                  if (!Number.isNaN(ts)) updateFilter('from', ts);
                }}
                placeholder="YYYY/MM/DD"
                openCalendarAriaLabel={(d): string => `${t('auditBilling.filter.from')}${d ? `, ${d}` : ''}`}
              />
            </FormField>
            <FormField label={t('auditBilling.filter.to')}>
              <DatePicker
                value={dateToInput(filters.to)}
                onChange={({ detail }): void => {
                  const ts = Date.parse(detail.value);
                  if (!Number.isNaN(ts)) updateFilter('to', ts + DAY_MS - 1);
                }}
                placeholder="YYYY/MM/DD"
                openCalendarAriaLabel={(d): string => `${t('auditBilling.filter.to')}${d ? `, ${d}` : ''}`}
              />
            </FormField>
            <FormField label={t('auditBilling.filter.extEventId')}>
              <Input
                value={filters.externalEventId ?? ''}
                onChange={({ detail }): void => {
                  updateFilter('externalEventId', detail.value || null);
                }}
                placeholder="evt_abc123"
              />
            </FormField>
          </ColumnLayout>
        </Container>

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && (
          <Alert
            type="error"
            action={
              <Button
                onClick={(): void => {
                  void fetchInitial();
                }}
              >
                {t('auditBilling.error.retry')}
              </Button>
            }
          >
            {t('auditBilling.error.load')}
          </Alert>
        )}
        {state.kind === 'READY' && (
          <SpaceBetween size="s">
            <Table<AuditBillingEvent>
              items={state.rows}
              trackBy="eventId"
              variant="container"
              empty={<Box textAlign="center" color="inherit">{t('auditBilling.empty')}</Box>}
              columnDefinitions={[
                {
                  id: 'at',
                  header: t('auditBilling.filter.from'),
                  cell: (row): React.ReactNode => new Date(row.at).toLocaleString(dateLocale),
                },
                {
                  id: 'tenant',
                  header: t('auditBilling.detail.tenant'),
                  cell: (row): React.ReactNode => (
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{String(row.tenantId)}</span>
                  ),
                },
                { id: 'action', header: t('auditBilling.filter.action'), cell: (row): React.ReactNode => row.action },
                {
                  id: 'actor',
                  header: t('auditBilling.detail.actor'),
                  cell: (row): React.ReactNode => `${row.actorType} · ${row.actorId.slice(0, 12)}`,
                },
                {
                  id: 'amount',
                  header: t('auditBilling.detail.metadata'),
                  cell: (row): React.ReactNode =>
                    row.action === 'GUARD_BLOCKED' ? (
                      <Badge color="red">{t('auditBilling.guardBlocked')}</Badge>
                    ) : row.amountCents !== null && row.currency !== null ? (
                      <span style={{ fontFamily: 'monospace' }}>
                        {row.currency} {(row.amountCents / 100).toFixed(2)}
                      </span>
                    ) : (
                      '—'
                    ),
                },
                {
                  id: 'actions',
                  header: '',
                  cell: (row): React.ReactNode => (
                    <Button
                      variant="inline-link"
                      onClick={(): void => {
                        setSelected(row);
                      }}
                    >
                      {t('auditBilling.detail.title', { defaultValue: 'Detalhes' })}
                    </Button>
                  ),
                },
              ]}
            />
            {state.hasMore && (
              <Box textAlign="center">
                <Button
                  onClick={(): void => {
                    void loadMore();
                  }}
                >
                  {t('auditBilling.loadMore')}
                </Button>
              </Box>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>

      <EventDetailDialog
        event={selected}
        onClose={(): void => {
          setSelected(null);
        }}
      />
    </ContentLayout>
  );
}

/** Modal com todos os campos do evento, incluindo `before`/`after`/`metadata`. */
function EventDetailDialog({
  event,
  onClose,
}: {
  event: AuditBillingEvent | null;
  onClose: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (event === null) return null;
  const kv = (label: string, value: React.ReactNode): React.JSX.Element => (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{value}</div>
    </div>
  );
  const mono = (v: React.ReactNode): React.JSX.Element => (
    <span style={{ fontFamily: 'monospace' }}>{v}</span>
  );
  return (
    <Modal
      visible
      onDismiss={onClose}
      size="large"
      header={`${event.action} — ${event.eventId.slice(0, 12)}`}
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={2} variant="text-grid">
          {kv(t('auditBilling.detail.tenant'), mono(String(event.tenantId)))}
          {kv(t('auditBilling.detail.at'), new Date(event.at).toISOString())}
          {kv(t('auditBilling.detail.actor'), <span>{event.actorType} · {mono(event.actorId)}</span>)}
          {kv(t('auditBilling.detail.subscription'), mono(event.subscriptionId ?? '—'))}
          {kv(t('auditBilling.detail.externalEvent'), mono(event.externalEventId ?? '—'))}
          {kv(t('auditBilling.detail.userAgent'), event.actorUserAgent ?? '—')}
          {kv(t('auditBilling.detail.ipHash'), mono(event.actorIpHash ?? '—'))}
        </ColumnLayout>
        {event.reason !== null && event.reason !== '' && kv(t('auditBilling.detail.reason'), event.reason)}
        <JsonBlock label={t('auditBilling.detail.before')} value={event.before} />
        <JsonBlock label={t('auditBilling.detail.after')} value={event.after} />
        <JsonBlock label={t('auditBilling.detail.metadata')} value={event.metadata} />
      </SpaceBetween>
    </Modal>
  );
}

/** Campo somente-leitura com o JSON formatado (até 10 linhas visíveis). */
function JsonBlock({ label, value }: { label: string; value: unknown }): React.JSX.Element {
  if (value === null || value === undefined) {
    return (
      <FormField label={label}>
        <Box color="text-status-inactive">—</Box>
      </FormField>
    );
  }
  const formatted = JSON.stringify(value, null, 2);
  return (
    <FormField label={label}>
      <Textarea value={formatted} readOnly rows={Math.min(10, formatted.split('\n').length)} />
    </FormField>
  );
}

/** Query string → `AuditBillingFilters`; data inválida cai na janela default de 30 dias. */
function parseFilters(params: URLSearchParams): AuditBillingFilters {
  const now = serverNow();
  const defaultFrom = now - DEFAULT_WINDOW_DAYS * DAY_MS;
  const fromParam = params.get('from');
  const toParam = params.get('to');
  const from = fromParam !== null ? Date.parse(fromParam) : defaultFrom;
  const toStart = toParam !== null ? Date.parse(toParam) : now;
  const to = toStart + DAY_MS - 1;
  const tenantId = params.get('tenantId');
  const action = params.get('action') as AuditBillingAction | null;
  const actorType = params.get('actorType') as AuditActorType | null;
  const extEventId = params.get('extEventId');
  return {
    from: Number.isNaN(from) ? defaultFrom : from,
    to: Number.isNaN(to) ? now : to,
    tenantId: tenantId !== null && tenantId.length > 0 ? tenantId : null,
    action: action !== null && action.length > 0 ? action : null,
    actorType: actorType !== null && actorType.length > 0 ? actorType : null,
    externalEventId: extEventId !== null && extEventId.length > 0 ? extEventId : null,
  };
}

/** Nome do parâmetro na URL (`externalEventId` vira `extEventId`). */
function filterKeyToParam(key: keyof AuditBillingFilters): string {
  if (key === 'externalEventId') return 'extEventId';
  return key;
}

function dateToInput(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

/** Baixa o CSV como arquivo via `Blob` + link temporário. */
function downloadCsv(csv: string, generatedAt: number): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-billing-${new Date(generatedAt).toISOString().replace(/[:.]/g, '-')}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Código de uma `HttpsError` vinda do SDK de Functions, sem o prefixo
 * `functions/` (`functions/resource-exhausted` → `resource-exhausted`).
 * Erro sem `code` string devolve `'unknown'`.
 *
 * Origem: #935 (item 16) — 2026-10-13 10:31 +0200. Usado por: `exportCsv`
 * deste arquivo (teste: `AuditBillingPage.test.tsx`).
 */
function callableErrorCode(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code.replace(/^functions\//, '') : 'unknown';
}
